
import {
  MusicalKey,
  TempoProfile,
  KeyTempoSuggestion,
  ProductionConfig,
  ProductionOutput,
  InstrumentStack,
  Instrument
} from '@/types/production.types';

import { getMatchingGenres, blendGenreProfiles, GENRE_PROFILES } from '@data/genreProfiles';
import { getMatchingMoods, getMusicalKey, MOOD_KEY_MAPPINGS, MUSICAL_KEYS } from '@data/moodKeyMappings';
import { getInstrumentById, getInstrumentsByIds, INSTRUMENT_LIBRARY } from '@data/instrumentLibrary';
// MIX_TEMPLATES now served via useMixTemplates hook; utility functions accept templates as parameter
import { getPacingForTempo } from '@data/pacingProfiles';

export const TEMPO_PROFILES: TempoProfile[] = [
  { id: 'funeral-doom', label: 'Funeral / Doom', bpmRange: [30, 55], suggestedBpm: 45, feel: 'crushing', genres: ['doom'], energyLevel: 2 },
  { id: 'slow-burn', label: 'Slow Burn / Ballad', bpmRange: [56, 72], suggestedBpm: 65, feel: 'emotional', genres: ['ballad'], energyLevel: 3 },
  { id: 'laid-back-groove', label: 'Laid-Back Groove', bpmRange: [73, 90], suggestedBpm: 82, feel: 'relaxed', genres: ['blues'], energyLevel: 4 },
  { id: 'mid-tempo', label: 'Mid-Tempo Rock', bpmRange: [91, 110], suggestedBpm: 100, feel: 'driving', genres: ['rock'], energyLevel: 6 },
  { id: 'driving-rock', label: 'Driving Rock', bpmRange: [111, 130], suggestedBpm: 120, feel: 'powerful', genres: ['rock'], energyLevel: 7 },
  { id: 'up-tempo', label: 'Up-Tempo Energy', bpmRange: [131, 155], suggestedBpm: 140, feel: 'energetic', genres: ['punk'], energyLevel: 8 },
  { id: 'high-energy', label: 'High Energy / Punk', bpmRange: [156, 180], suggestedBpm: 170, feel: 'frantic', genres: ['punk'], energyLevel: 9 },
  { id: 'thrash', label: 'Thrash / Speed', bpmRange: [181, 220], suggestedBpm: 200, feel: 'relentless', genres: ['thrash'], energyLevel: 10 }
];

export function getTempoProfileForBpm(bpm: number): TempoProfile {
  const profile = TEMPO_PROFILES.find(p => bpm >= p.bpmRange[0] && bpm <= p.bpmRange[1]);
  return profile || TEMPO_PROFILES[3];
}

export function getTempoProfileById(id: string): TempoProfile | undefined {
  return TEMPO_PROFILES.find(p => p.id === id);
}

export function generateKeyTempoSuggestion(musicalBlendKeywords: string[], moodKeywords: string[]): KeyTempoSuggestion {
  const matchedGenres = getMatchingGenres(musicalBlendKeywords);
  const blendedGenre = blendGenreProfiles(matchedGenres);
  const matchedMoods = getMatchingMoods(moodKeywords);
  const suggestedKey = calculateOptimalKey(blendedGenre.preferredKeys, blendedGenre.preferredModes, matchedMoods);
  const suggestedTempo = calculateOptimalTempo(blendedGenre.tempoRange, blendedGenre.suggestedTempo, matchedMoods);
  const tempoProfile = getTempoProfileForBpm(suggestedTempo);
  const moodMatchScore = calculateMoodMatchScore(suggestedKey, suggestedTempo, matchedMoods);
  const genreFitScore = calculateGenreFitScore(suggestedKey, suggestedTempo, matchedGenres);
  const alternativeKeys = generateAlternativeKeys(suggestedKey, blendedGenre.preferredKeys, blendedGenre.preferredModes);
  const alternativeTempos = generateAlternativeTempos(suggestedTempo, blendedGenre.tempoRange);
  const reasoning = generateReasoning(suggestedKey, suggestedTempo, matchedGenres, matchedMoods);
  return { suggestedKey, suggestedTempo, tempoProfile, moodMatchScore, genreFitScore, reasoning, alternativeKeys, alternativeTempos };
}

function calculateOptimalKey(genreKeys: string[], genreModes: string[], moods: any[]): MusicalKey {
  const keyScores: Record<string, number> = {};
  const modeScores: Record<string, number> = {};
  genreKeys.forEach((key, index) => { keyScores[key] = (keyScores[key] || 0) + (genreKeys.length - index) * 2; });
  genreModes.forEach((mode, index) => { modeScores[mode] = (modeScores[mode] || 0) + (genreModes.length - index) * 2; });
  moods.forEach(mood => {
    mood.primaryKeys.forEach((key: string, index: number) => { keyScores[key] = (keyScores[key] || 0) + (mood.primaryKeys.length - index) * 3; });
    mood.primaryModes.forEach((mode: string, index: number) => { modeScores[mode] = (modeScores[mode] || 0) + (mood.primaryModes.length - index) * 3; });
  });
  const bestKey = Object.entries(keyScores).sort((a, b) => b[1] - a[1])[0]?.[0] || 'E';
  const bestMode = Object.entries(modeScores).sort((a, b) => b[1] - a[1])[0]?.[0] || 'minor';
  return getMusicalKey(bestKey, bestMode) || { root: bestKey, mode: bestMode as any, displayName: `${bestKey} ${bestMode}`, commonChords: [], emotionalProfile: [] };
}

function calculateOptimalTempo(range: [number, number], suggested: number, moods: any[]): number {
  let tempo = suggested;
  let slow = 0, fast = 0, mid = 0;
  moods.forEach(m => { if (m.tempoTendency === 'slow') slow++; else if (m.tempoTendency === 'fast') fast++; else mid++; });
  if (slow > fast && slow > mid) tempo = Math.max(range[0], tempo - 15);
  else if (fast > slow && fast > mid) tempo = Math.min(range[1], tempo + 15);
  if (moods.length > 0) {
    const avgEnergy = moods.reduce((sum, m) => sum + (m.energyRange[0] + m.energyRange[1]) / 2, 0) / moods.length;
    tempo = Math.round(tempo + (avgEnergy - 5) * 3);
  }
  return Math.max(range[0], Math.min(range[1], tempo));
}

function calculateMoodMatchScore(key: MusicalKey, tempo: number, moods: any[]): number {
  if (moods.length === 0) return 75;
  let score = 0;
  moods.forEach(m => {
    if (m.primaryKeys.includes(key.root)) score += 25;
    if (m.primaryModes.includes(key.mode)) score += 25;
    const tp = getTempoProfileForBpm(tempo);
    score += Math.max(0, 25 - Math.abs(tp.energyLevel - (m.energyRange[0] + m.energyRange[1]) / 2) * 5);
  });
  return Math.min(100, Math.round((score / moods.length) + 25));
}

function calculateGenreFitScore(key: MusicalKey, tempo: number, genres: any[]): number {
  if (genres.length === 0) return 75;
  let score = 0;
  genres.forEach(g => {
    if (g.preferredKeys.includes(key.root)) score += 35;
    if (g.preferredModes.includes(key.mode)) score += 20;
    if (tempo >= g.tempoRange[0] && tempo <= g.tempoRange[1]) score += 45;
  });
  return Math.min(100, Math.round(score / genres.length));
}

function generateAlternativeKeys(primary: MusicalKey, gKeys: string[], gModes: string[]): MusicalKey[] {
  const alts: MusicalKey[] = [];
  if (primary.relativeKey) {
    const [r, m] = primary.relativeKey.split(' ');
    const k = getMusicalKey(r, m.toLowerCase());
    if (k) alts.push(k);
  }
  gKeys.slice(0, 3).forEach(root => {
    if (root !== primary.root) {
      gModes.forEach(mode => {
        const k = getMusicalKey(root, mode);
        if (k && !alts.find(a => a.root === k.root && a.mode === k.mode)) alts.push(k);
      });
    }
  });
  return alts.slice(0, 4);
}

function generateAlternativeTempos(primary: number, range: [number, number]): TempoProfile[] {
  return TEMPO_PROFILES.filter(p => p.bpmRange[1] >= range[0] && p.bpmRange[0] <= range[1] && (primary < p.bpmRange[0] || primary > p.bpmRange[1])).slice(0, 3);
}

function generateReasoning(key: MusicalKey, tempo: number, genres: any[], moods: any[]): string {
  const parts: string[] = [];
  parts.push(`${key.displayName} provides a ${key.emotionalProfile.slice(0, 2).join(' and ')} foundation`);
  if (genres.length > 0) parts.push(`aligns well with ${genres.slice(0, 2).map(g => g.name).join(' and ')}`);
  const tp = getTempoProfileForBpm(tempo);
  parts.push(`${tempo} BPM (${tp.label}) delivers a ${tp.feel} energy`);
  if (moods.length > 0) parts.push(`matching the ${moods.slice(0, 2).map(m => m.mood).join(', ')} mood architecture`);
  return parts.join(', ') + '.';
}

export function buildInstrumentStack(musicalBlendKeywords: string[]): InstrumentStack {
  const matchedGenres = getMatchingGenres(musicalBlendKeywords);
  const blended = blendGenreProfiles(matchedGenres);
  const coreInstruments = getInstrumentsByIds(blended.coreInstruments);
  const colorInstruments = getInstrumentsByIds(blended.colorInstruments);
  const seen = new Set<string>();
  const core: Instrument[] = [], color: Instrument[] = [], texture: Instrument[] = [];
  coreInstruments.forEach(inst => { if (!seen.has(inst.id)) { seen.add(inst.id); core.push(inst); } });
  colorInstruments.forEach(inst => { if (!seen.has(inst.id)) { seen.add(inst.id); color.push(inst); } });
  matchedGenres.forEach(genre => {
    genre.textureInstruments.forEach(instId => {
      if (!seen.has(instId)) {
        const inst = getInstrumentById(instId);
        if (inst) { seen.add(instId); texture.push(inst); }
      }
    });
  });
  return { core, color, texture, optional: [] };
}

export function getRecommendedAdditions(currentStack: InstrumentStack, musicalBlendKeywords: string[]): Instrument[] {
  const matchedGenres = getMatchingGenres(musicalBlendKeywords);
  const currentIds = new Set([...currentStack.core.map(i => i.id), ...currentStack.color.map(i => i.id), ...currentStack.texture.map(i => i.id)]);
  const recommendations: Instrument[] = [];
  matchedGenres.forEach(genre => {
    [...genre.colorInstruments, ...genre.textureInstruments].forEach(instId => {
      if (!currentIds.has(instId)) {
        const inst = getInstrumentById(instId);
        if (inst && !recommendations.find(r => r.id === inst.id)) recommendations.push(inst);
      }
    });
  });
  return recommendations.slice(0, 6);
}

export function generateProductionOutput(config: ProductionConfig): ProductionOutput {
  const { selectedKey, selectedTempo, instrumentStack, mixTemplate } = config;
  const preamble = `This is a high-fidelity analog capture of a "live in the room" session. The signal chain focuses on weight and transient punch to merge Southern authenticity with studio precision.`;
  const keyBlock = selectedKey ? `[Key Signature: ${selectedKey.displayName} | Mode: ${selectedKey.mode} | Emotional Color: ${selectedKey.emotionalProfile.join(', ')}]` : '';
  let tempoBlock = '';
  if (selectedTempo) {
    const profile = getTempoProfileForBpm(selectedTempo);
    const pacing = getPacingForTempo(selectedTempo);
    tempoBlock = `[Tempo: ${selectedTempo} BPM (${profile.label}) | Pacing: Phrases: ${pacing.phraseLength} | Density: ${pacing.syllableDensity} | Dynamics: ${pacing.dynamicRange}]`;
  }
  const categoryGroups: Record<string, Instrument[]> = { 'Drums (Room & Kit)': [], 'Bass': [], 'Guitars (Dual Attack)': [], 'Keys / Organ': [], 'Atmosphere & Texture': [] };
  [...instrumentStack.core, ...instrumentStack.color, ...instrumentStack.texture].forEach(inst => {
    if (inst.category === 'drums') categoryGroups['Drums (Room & Kit)'].push(inst);
    else if (inst.category === 'bass') categoryGroups['Bass'].push(inst);
    else if (inst.category === 'rhythm' || inst.category === 'lead') categoryGroups['Guitars (Dual Attack)'].push(inst);
    else if (inst.category === 'keys') categoryGroups['Keys / Organ'].push(inst);
    else categoryGroups['Atmosphere & Texture'].push(inst);
  });
  const instrumentBlocks: string[] = [];
  Object.entries(categoryGroups).forEach(([label, insts]) => {
    if (insts.length > 0) {
      const details = insts.map((i, idx) => label.includes('Guitars') ? `${idx + 1}. ${i.name}: ${i.promptHints}. ${i.mixNotes}` : `${i.name}: ${i.promptHints}. ${i.mixNotes}`).join(' ');
      instrumentBlocks.push(`[${label}: ${details}]`);
    }
  });
  const instrumentBlockText = instrumentBlocks.join('\n\n');
  let mixBlockText = '';
  if (mixTemplate) {
    const positions = mixTemplate.positions.map(pos => {
      const inst = getInstrumentById(pos.instrumentId);
      if (!inst) return null;
      const panLabel = pos.pan === 0 ? 'CENTER' : pos.pan < 0 ? `${Math.abs(pos.pan)}% L` : `${pos.pan}% R`;
      return `${inst.name} (${panLabel}, ${pos.depth} depth, ${pos.level})`;
    }).filter(Boolean);
    mixBlockText = `[Stereo Field Simulation: ${mixTemplate.name}. Positioning: ${positions.join(' | ')}]`;
  }
  let productionSignatureBlockText = '';
  const sig = mixTemplate?.productionSignature;
  if (sig) {
    productionSignatureBlockText = `[Production Character: ${sig.ampCharacter} Gain staging: ${sig.gainStaging}. Capture approach: ${sig.captureApproach} Dynamics: ${sig.dynamicApproach} Era texture: ${sig.eraTexture}]`;
  }
  // Mastering block (injected externally via masteringBlock param)
  const masteringBlock = (config as any).masteringBlock as string | undefined;

  const allBlocks = [preamble, keyBlock, tempoBlock, instrumentBlockText, mixBlockText, productionSignatureBlockText];
  if (masteringBlock) allBlocks.push(masteringBlock);
  const fullPromptBlock = `[PRODUCTION INTELLIGENCE ARCHITECTURE]\n\n${allBlocks.filter(Boolean).join('\n\n')}\n\n[END PRODUCTION ARCHITECTURE]`;
  const compactParts: string[] = [];
  if (selectedKey) compactParts.push(`Key: ${selectedKey.displayName}`);
  if (selectedTempo) compactParts.push(`${selectedTempo} BPM ${getTempoProfileForBpm(selectedTempo).feel}`);
  if (instrumentStack.core.length > 0) compactParts.push(`Core: ${instrumentStack.core.map(i => i.name).slice(0, 4).join(', ')}`);
  const sunoTags: string[] = [];
  if (selectedKey?.mode === 'minor') sunoTags.push('minor key');
  if (selectedTempo && selectedTempo < 70) sunoTags.push('slow tempo');
  if (selectedTempo && selectedTempo > 140) sunoTags.push('fast tempo');

  // Humanization tag injection
  const humanization = config.humanization ?? 0;
  let humanizationTags: string[] = [];
  if (humanization >= 1 && humanization <= 20) {
    humanizationTags = ['tight production', 'precise'];
  } else if (humanization >= 21 && humanization <= 40) {
    humanizationTags = ['studio live feel'];
  } else if (humanization >= 41 && humanization <= 60) {
    humanizationTags = ['slight swing', 'human feel'];
  } else if (humanization >= 61 && humanization <= 80) {
    humanizationTags = ['loose groove', 'raw recording', 'behind the beat'];
  } else if (humanization >= 81) {
    humanizationTags = ['lo-fi imperfection', 'garage recording', 'drunken swing'];
  }
  sunoTags.push(...humanizationTags);

  // Append humanization line to fullPromptBlock
  let finalPromptBlock = fullPromptBlock;
  if (humanization > 0 && humanizationTags.length > 0) {
    finalPromptBlock = fullPromptBlock.replace(
      '[END PRODUCTION ARCHITECTURE]',
      `[Humanization: ${humanization}% — ${humanizationTags.join(', ')}]\n\n[END PRODUCTION ARCHITECTURE]`
    );
  }

  return { keyBlock, tempoBlock, instrumentBlock: instrumentBlockText, mixBlock: mixBlockText, productionSignatureBlock: productionSignatureBlockText || undefined, masteringBlock: masteringBlock || undefined, fullPromptBlock: finalPromptBlock, compactPrompt: compactParts.join('. ') + '.', sunoStyleTags: sunoTags };
}
